import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { LandingPage } from './components/LandingPage';
import { Footer } from './components/Footer';
import { Loader2 } from 'lucide-react';

// Lazy load components for code splitting
const AuthPage = lazy(() => import('./components/AuthPage').then(m => ({ default: m.AuthPage })));
const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const Dashboard = lazy(() => import('./components/Dashboard').then(m => ({ default: m.Dashboard })));
const SuperAdminDashboard = lazy(() => import('./components/SuperAdminDashboard').then(m => ({ default: m.SuperAdminDashboard })));
const SignupPage = lazy(() => import('./components/SignupPage').then(m => ({ default: m.SignupPage })));
const SignupSuccess = lazy(() => import('./components/SignupSuccess').then(m => ({ default: m.SignupSuccess })));
const TermsPage = lazy(() => import('./components/TermsPage').then(m => ({ default: m.TermsPage })));
const PrivacyPolicyPage = lazy(() => import('./components/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const UnifiedDocsPage = lazy(() => import('./components/help/UnifiedDocsPage').then(m => ({ default: m.UnifiedDocsPage })));
const ActivatePage = lazy(() => import('./components/ActivatePage').then(m => ({ default: m.ActivatePage })));
const HelpLayout = lazy(() => import('./components/help-archive/HelpLayout').then(m => ({ default: m.HelpLayout })));
const IntroPage = lazy(() => import('./components/help-archive/pages/IntroPage').then(m => ({ default: m.IntroPage })));
const ChatModesPage = lazy(() => import('./components/help-archive/pages/ChatModesPage').then(m => ({ default: m.ChatModesPage })));
const WebhookPage = lazy(() => import('./components/help-archive/pages/WebhookPage').then(m => ({ default: m.WebhookPage })));
const PayloadPage = lazy(() => import('./components/help-archive/pages/PayloadPage').then(m => ({ default: m.PayloadPage })));
const VoicePage = lazy(() => import('./components/help-archive/pages/VoicePage').then(m => ({ default: m.VoicePage })));
const CameraPage = lazy(() => import('./components/help-archive/pages/CameraPage').then(m => ({ default: m.CameraPage })));
const DocumentsPage = lazy(() => import('./components/help-archive/pages/DocumentsPage').then(m => ({ default: m.DocumentsPage })));
const LabelsPage = lazy(() => import('./components/help-archive/pages/LabelsPage').then(m => ({ default: m.LabelsPage })));
const QRScannerPage = lazy(() => import('./components/help-archive/pages/QRScannerPage').then(m => ({ default: m.QRScannerPage })));
const MetadataPage = lazy(() => import('./components/help-archive/pages/MetadataPage').then(m => ({ default: m.MetadataPage })));
const PrivacyPage = lazy(() => import('./components/help-archive/pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const HostingPage = lazy(() => import('./components/help-archive/pages/HostingPage').then(m => ({ default: m.HostingPage })));
const IntegrationsPage = lazy(() => import('./components/help-archive/pages/IntegrationsPage').then(m => ({ default: m.IntegrationsPage })));
const HomeAutomationPage = lazy(() => import('./components/help-archive/pages/HomeAutomationPage').then(m => ({ default: m.HomeAutomationPage })));
const PushNotificationsPage = lazy(() => import('./components/help-archive/pages/PushNotificationsPage').then(m => ({ default: m.PushNotificationsPage })));
const EnterpriseIntroPage = lazy(() => import('./components/help-archive/pages/EnterpriseIntroPage').then(m => ({ default: m.EnterpriseIntroPage })));
const OnboardingPage = lazy(() => import('./components/help-archive/pages/OnboardingPage').then(m => ({ default: m.OnboardingPage })));
const ConfigPage = lazy(() => import('./components/help-archive/pages/ConfigPage').then(m => ({ default: m.ConfigPage })));
const HeartbeatPage = lazy(() => import('./components/help-archive/pages/HeartbeatPage').then(m => ({ default: m.HeartbeatPage })));
const SupportPage = lazy(() => import('./components/help-archive/pages/SupportPage').then(m => ({ default: m.SupportPage })));
const IndexPage = lazy(() => import('./components/help-archive/pages/IndexPage').then(m => ({ default: m.IndexPage })));

const LoadingFallback = () => (
  <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
  </div>
);

function App() {
  const { user, loading, userRole, viewingCompanyId, viewingAgencyId } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const showFooter = !user;

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex-1">
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/signup/success" element={<SignupSuccess />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/help" element={<UnifiedDocsPage />} />
            <Route path="/activate/*" element={<ActivatePage />} />
            <Route path="/help-old" element={<HelpLayout />}>
              <Route index element={<IndexPage />} />
              <Route path="intro" element={<IntroPage />} />
              <Route path="chat-modes" element={<ChatModesPage />} />
              <Route path="webhook" element={<WebhookPage />} />
              <Route path="payload" element={<PayloadPage />} />
              <Route path="voice" element={<VoicePage />} />
              <Route path="camera" element={<CameraPage />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="labels" element={<LabelsPage />} />
              <Route path="qr-scanner" element={<QRScannerPage />} />
              <Route path="metadata" element={<MetadataPage />} />
              <Route path="privacy" element={<PrivacyPage />} />
              <Route path="hosting" element={<HostingPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="home-automation" element={<HomeAutomationPage />} />
              <Route path="push-notifications" element={<PushNotificationsPage />} />
              <Route path="enterprise-intro" element={<EnterpriseIntroPage />} />
              <Route path="onboarding" element={<OnboardingPage />} />
              <Route path="config" element={<ConfigPage />} />
              <Route path="heartbeat" element={<HeartbeatPage />} />
              <Route path="support" element={<SupportPage />} />
            </Route>
            <Route path="/helpcenter" element={<Navigate to="/help" replace />} />
            <Route
              path="/*"
              element={<MainApp />}
            />
          </Routes>
        </Suspense>
      </div>
      {showFooter && <Footer />}
    </div>
  );
}

function MainApp() {
  const navigate = useNavigate();
  const { user, userRole, viewingCompanyId, viewingAgencyId, signOut } = useAuth();

  if (!user) {
    return <LandingPage onGetStarted={() => navigate('/signup')} onLogin={() => navigate('/login')} />;
  }


  if (userRole === 'owner') {
    if (viewingCompanyId) {
      return <Dashboard />;
    }
    if (viewingAgencyId) {
      return <SuperAdminDashboard />;
    }
    return <SuperAdminDashboard />;
  }

  if (userRole === 'agency_admin') {
    if (viewingCompanyId) {
      return <Dashboard />;
    }
    return <SuperAdminDashboard />;
  }

  return <Dashboard />;
}

export default App;
